import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useKeycloak } from '@/hooks/use-keycloak-wrapper'
import { useAuth } from '@/hooks/use-auth'
import { setTokenProvider } from '@/services/api'
import { Toaster } from '@/components/ui/toaster'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PageErrorFallback } from '@/components/PageErrorFallback'
import { ThemeProvider } from '@/components/theme-provider'
import { SchoolContextProvider } from '@/contexts/SchoolContext'
import { MainLayout } from '@/components/layouts/main-layout'
import { RoleGuard } from '@/components/RoleGuard'
import { LoadingScreen } from '@/components/ui/loading-screen'

// Pages
import { LandingPage } from '@/pages/landing'
import { LoginPage } from '@/pages/auth/login'
import { RegisterPage } from '@/pages/auth/register'
import { OnboardingSelectionPage } from '@/pages/onboarding/OnboardingSelectionPage'
const OnboardingRouter = React.lazy(() => import('@/components/onboarding/OnboardingRouter').then(m => ({ default: m.OnboardingRouter })))
const InteractiveDemo = React.lazy(() => import('@/components/onboarding/InteractiveDemo').then(m => ({ default: m.InteractiveDemo })))
import { DashboardPage } from '@/pages/dashboard'
const StudentsPage = React.lazy(() => import('@/pages/students').then(m => ({ default: m.StudentsPage })))
const TeachersPage = React.lazy(() => import('@/pages/teachers').then(m => ({ default: m.TeachersPage })))
const DevTestPage = React.lazy(() => import('@/pages/DevTestPage'))
const SimpleLandingPage = React.lazy(() => import('@/pages/SimpleLandingPage'))
const LessonsPage = React.lazy(() => import('@/pages/lessons').then(m => ({ default: m.LessonsPage })))
const AssessmentsPage = React.lazy(() => import('@/pages/assessments').then(m => ({ default: m.AssessmentsPage })))
const ProfilePage = React.lazy(() => import('@/pages/profile').then(m => ({ default: m.ProfilePage })))
const SettingsPage = React.lazy(() => import('@/pages/settings').then(m => ({ default: m.SettingsPage })))
const DesignSystemPage = React.lazy(() => import('@/pages/design-system').then(m => ({ default: m.DesignSystemPage })))
const ParentDashboard = React.lazy(() => import('@/pages/parent/ParentDashboard').then(m => ({ default: m.ParentDashboard })))
import EmailVerificationPage from '@/pages/auth/EmailVerificationPage'
import ResendVerificationPage from '@/pages/auth/ResendVerificationPage'
import { LoginPageWithVerification } from '@/pages/auth/LoginWithVerification'
import { ForgotPassword } from '@/pages/auth/ForgotPassword'
import { ResetPassword } from '@/pages/auth/ResetPassword'
const ExamPrepPage = React.lazy(() => import('@/pages/exam-prep').then(m => ({ default: m.ExamPrepPage })))
const CatalogReviewPage = React.lazy(() => import('@/pages/CatalogReviewPage').then(m => ({ default: m.CatalogReviewPage })))
const AyaInboxPage = React.lazy(() => import('@/pages/AyaInboxPage').then(m => ({ default: m.AyaInboxPage })))
const GamificationPage = React.lazy(() => import('@/pages/gamification').then(m => ({ default: m.GamificationPage })))
const AnalyticsPage = React.lazy(() => import('@/pages/analytics').then(m => ({ default: m.AnalyticsPage })))
const LanguageTestPage = React.lazy(() => import('@/pages/language-test').then(m => ({ default: m.LanguageTestPage })))
const LanguageTest = React.lazy(() => import('@/components/test/LanguageTest').then(m => ({ default: m.LanguageTest })))
const FreemiumSignup = React.lazy(() => import('@/pages/FreemiumSignup').then(m => ({ default: m.FreemiumSignup })))
import { useTrialAuth } from '@/hooks/use-trial-auth'
import { EmailVerification } from '@/pages/EmailVerification'
import { ParentalConsentPage } from '@/pages/consent/ParentalConsentPage'
const CommunicationPage = React.lazy(() => import('@/pages/communication/CommunicationPage').then(m => ({ default: m.CommunicationPage })))
const AITutorPage = React.lazy(() => import('@/pages/ai-tutor/AITutorPage').then(m => ({ default: m.AITutorPage })))
const LessonPlannerPage = React.lazy(() => import('@/pages/teacher-copilot/LessonPlannerPage'))
import { NotFound } from '@/pages/NotFound'
const SchoolManagementPage = React.lazy(() => import('@/pages/school-management/SchoolManagementPage').then(m => ({ default: m.SchoolManagementPage })))
const GradeEntryPage = React.lazy(() => import('@/pages/school-management/GradeEntryPage').then(m => ({ default: m.GradeEntryPage })))
const ParentReportCardPage = React.lazy(() => import('@/pages/school-management/ParentReportCardPage').then(m => ({ default: m.ParentReportCardPage })))
const AdminSetupWizard = React.lazy(() => import('@/pages/school-management/AdminSetupWizard'))
const AttendanceScannerPage = React.lazy(() => import('@/pages/attendance').then(m => ({ default: m.AttendanceScannerPage })))
const CafeteriaPage = React.lazy(() => import('@/pages/cafeteria').then(m => ({ default: m.CafeteriaPage })))
const EMFPortal = React.lazy(() => import('@/pages/emf-portal'))
const TeacherPayslips = React.lazy(() => import('@/pages/teacher/TeacherPayslips'))
const TeachingRecordPage = React.lazy(() => import('@/pages/teacher/TeachingRecordPage'))
const TeacherCalendar = React.lazy(() => import('@/pages/teacher/TeacherCalendar'))
const GradeHistoryPage = React.lazy(() => import('@/pages/school-management/GradeHistoryPage'))
const TeacherAttendancePage = React.lazy(() => import('@/pages/school-management/AttendanceManager').then(m => ({ default: m.AttendanceManager })))
const TeacherHomeworkPage = React.lazy(() => import('@/pages/school-management/HomeworkManager').then(m => ({ default: m.HomeworkManager })))
const TeacherSuiviPage = React.lazy(() => import('@/pages/school-management/SuiviPedagogique').then(m => ({ default: m.SuiviPedagogique })))
const UserManagementPage = React.lazy(() => import('@/pages/school-management/UserManagementPage').then(m => ({ default: m.UserManagementPage })))
const UpgradeSuccessPage = React.lazy(() => import('@/pages/upgrade/UpgradeSuccessPage'))
const AccountingDashboard = React.lazy(() => import('@/pages/accounting/AccountingDashboard'))
const PricingPage = React.lazy(() => import('@/pages/pricing'))
const JoinSchoolPage = React.lazy(() => import('@/pages/join-school').then(m => ({ default: m.JoinSchoolPage })))
const SupportPage = React.lazy(() => import('@/pages/support'))

// Create a token getter reference that can be updated
let currentTokenGetter: (() => string | null) | null = null

function App() {
  const { initialized, keycloak } = useKeycloak()
  const { isAuthenticated, isLoading, token } = useAuth()
  const { hasHydrated } = useTrialAuth()

  // Use a ref for token so the effect doesn't re-run on every token change
  const tokenRef = React.useRef(token)
  tokenRef.current = token

  // Set up token provider for API client once — uses ref to always read latest token
  React.useEffect(() => {
    if (hasHydrated || initialized) {
      currentTokenGetter = () => tokenRef.current || null
      setTokenProvider(() => currentTokenGetter?.() || null)
    }
  }, [hasHydrated, initialized])

  if (!initialized || isLoading) {
    return <LoadingScreen />
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="appacademia-theme">
      <SchoolContextProvider>
        <ErrorBoundary fallback={<PageErrorFallback />}>
        <React.Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login-traditional" element={<LoginPageWithVerification />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Freemium registration */}
          <Route path="/freemium-signup" element={<FreemiumSignup />} />

          {/* Email verification routes */}
          <Route path="/auth/verify-email" element={<EmailVerification />} />
          {/* A parent approves the account their child created */}
          <Route path="/consent/:token" element={<ParentalConsentPage />} />
          <Route path="/auth/resend-verification" element={<ResendVerificationPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />

          {/* New onboarding routes */}
          <Route path="/onboard" element={<OnboardingSelectionPage />} />
          <Route path="/onboard/*" element={<OnboardingRouter />} />
          <Route path="/demo" element={<InteractiveDemo />} />

          {/* Existing public routes */}
          <Route path="/design-system" element={<DesignSystemPage />} />
          {import.meta.env.DEV && <Route path="/dev-test" element={<DevTestPage />} />}
          <Route path="/simple" element={<SimpleLandingPage />} />
          <Route path="/lang-test" element={<LanguageTest />} />

          {/* Protected routes */}
          <Route
            path="/app"
            element={
              isAuthenticated ? (
                <MainLayout />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="language-test" element={<LanguageTestPage />} />
            <Route path="upgrade/success" element={<React.Suspense fallback={<LoadingScreen />}><UpgradeSuccessPage /></React.Suspense>} />
            <Route path="pricing" element={<React.Suspense fallback={<LoadingScreen />}><PricingPage /></React.Suspense>} />
            <Route path="support" element={<SupportPage />} />
            <Route path="join-school" element={<React.Suspense fallback={<LoadingScreen />}><JoinSchoolPage /></React.Suspense>} />

            {/* Student & Teacher: learning features */}
            <Route path="lessons" element={<RoleGuard allowed={['student', 'teacher']}><LessonsPage /></RoleGuard>} />
            <Route path="assessments" element={<RoleGuard allowed={['student', 'teacher']}><AssessmentsPage /></RoleGuard>} />
            <Route path="exam-prep" element={<RoleGuard allowed={['student', 'teacher', 'parent']}><ExamPrepPage /></RoleGuard>} />
            {/* catalog reviewers are teachers or admins; the page itself checks the reviewer role */}
            <Route path="catalog-review" element={<RoleGuard allowed={['teacher', 'admin']}><React.Suspense fallback={<LoadingScreen />}><CatalogReviewPage /></React.Suspense></RoleGuard>} />
            <Route path="aya-inbox" element={<RoleGuard allowed={['admin']}><React.Suspense fallback={<LoadingScreen />}><AyaInboxPage /></React.Suspense></RoleGuard>} />
            <Route path="gamification" element={<RoleGuard allowed={['student']}><GamificationPage /></RoleGuard>} />
            <Route path="analytics" element={<RoleGuard allowed={['student', 'teacher']}><AnalyticsPage /></RoleGuard>} />
            <Route path="ai-tutor" element={<RoleGuard allowed={['student', 'teacher', 'parent']}><AITutorPage /></RoleGuard>} />
            <Route path="lesson-planner" element={<RoleGuard allowed={['teacher']}><React.Suspense fallback={<LoadingScreen />}><LessonPlannerPage /></React.Suspense></RoleGuard>} />
            <Route path="communication" element={<RoleGuard allowed={['student', 'teacher', 'parent', 'admin', 'school_admin', 'secretary', 'surveillant_general']}><CommunicationPage /></RoleGuard>} />
            <Route path="report-cards" element={<RoleGuard allowed={['student', 'parent', 'teacher', 'admin', 'school_admin']}><ParentReportCardPage /></RoleGuard>} />

            {/* Teacher-only features */}
            <Route path="students" element={<RoleGuard allowed={['teacher', 'admin', 'school_admin', 'secretary', 'surveillant_general']}><StudentsPage /></RoleGuard>} />
            <Route path="teachers" element={<RoleGuard allowed={['admin', 'school_admin']}><TeachersPage /></RoleGuard>} />
            <Route path="grade-entry" element={<RoleGuard allowed={['teacher', 'admin', 'school_admin']}><GradeEntryPage /></RoleGuard>} />
            <Route path="grade-history" element={<RoleGuard allowed={['teacher', 'admin', 'school_admin']}><React.Suspense fallback={<LoadingScreen />}><GradeHistoryPage /></React.Suspense></RoleGuard>} />
            <Route path="teaching-record" element={<RoleGuard allowed={['teacher', 'admin', 'school_admin']}><React.Suspense fallback={<LoadingScreen />}><TeachingRecordPage /></React.Suspense></RoleGuard>} />
            <Route path="teacher-payslips" element={<RoleGuard allowed={['teacher']}><React.Suspense fallback={<LoadingScreen />}><TeacherPayslips /></React.Suspense></RoleGuard>} />
            <Route path="teacher-calendar" element={<RoleGuard allowed={['teacher']}><React.Suspense fallback={<LoadingScreen />}><TeacherCalendar /></React.Suspense></RoleGuard>} />
            <Route path="teacher-attendance" element={<RoleGuard allowed={['teacher']}><React.Suspense fallback={<LoadingScreen />}><TeacherAttendancePage /></React.Suspense></RoleGuard>} />
            <Route path="teacher-homework" element={<RoleGuard allowed={['teacher']}><React.Suspense fallback={<LoadingScreen />}><TeacherHomeworkPage /></React.Suspense></RoleGuard>} />
            <Route path="suivi" element={<RoleGuard allowed={['teacher', 'admin', 'school_admin']}><React.Suspense fallback={<LoadingScreen />}><TeacherSuiviPage /></React.Suspense></RoleGuard>} />

            {/* Parent-only features */}
            <Route path="parent" element={<RoleGuard allowed={['parent']}><ParentDashboard /></RoleGuard>} />
            <Route path="parent/communication" element={<RoleGuard allowed={['parent']}><Navigate to="/app/parent?tab=communication" replace /></RoleGuard>} />

            {/* Admin-only features */}
            <Route path="school-management" element={<RoleGuard allowed={['admin', 'school_admin', 'secretary']}><SchoolManagementPage /></RoleGuard>} />
            <Route path="admin-analytics" element={<RoleGuard allowed={['admin', 'school_admin']}><Navigate to="/app/school-management?tab=analytics" replace /></RoleGuard>} />
            <Route path="finance" element={<RoleGuard allowed={['admin', 'school_admin', 'accountant']}><Navigate to="/app/school-management?tab=fees" replace /></RoleGuard>} />
            <Route path="payroll" element={<RoleGuard allowed={['admin', 'school_admin', 'accountant']}><Navigate to="/app/school-management?tab=payroll" replace /></RoleGuard>} />
            <Route path="transport" element={<RoleGuard allowed={['admin', 'school_admin']}><Navigate to="/app/school-management?tab=transport" replace /></RoleGuard>} />
            <Route path="resources" element={<RoleGuard allowed={['admin', 'school_admin', 'secretary']}><Navigate to="/app/school-management?tab=documents" replace /></RoleGuard>} />
            <Route path="school-setup" element={<RoleGuard allowed={['admin', 'school_admin']}><Navigate to="/app/school-management?tab=setup" replace /></RoleGuard>} />
            <Route path="user-management" element={<RoleGuard allowed={['admin', 'school_admin']}><React.Suspense fallback={<LoadingScreen />}><UserManagementPage /></React.Suspense></RoleGuard>} />
            <Route path="admin-setup" element={<RoleGuard allowed={['admin', 'school_admin']}><AdminSetupWizard /></RoleGuard>} />

            {/* Operational roles: scanning + cafeteria */}
            <Route path="attendance" element={<RoleGuard allowed={['admin', 'school_admin', 'guard', 'bus_driver', 'surveillant_general']}><AttendanceScannerPage /></RoleGuard>} />
            <Route path="cafeteria" element={<RoleGuard allowed={['admin', 'school_admin', 'cafeteria_staff', 'parent']}><CafeteriaPage /></RoleGuard>} />

            {/* EMF Portal */}
            <Route path="emf-portal" element={<RoleGuard allowed={['emf_agent']}><React.Suspense fallback={<LoadingScreen />}><EMFPortal /></React.Suspense></RoleGuard>} />

            {/* Accounting */}
            <Route path="accounting" element={<RoleGuard allowed={['admin', 'school_admin', 'accountant']}><React.Suspense fallback={<LoadingScreen />}><AccountingDashboard /></React.Suspense></RoleGuard>} />

            {/* Catch-all inside /app */}
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </React.Suspense>
        </ErrorBoundary>
        <Toaster />
      </SchoolContextProvider>
    </ThemeProvider>
  )
}

export default App
